/**
 * Configuración del workflow EPP
 */

import { RetiroWorkflow } from './RetiroWorkflow';
import { TransferenciaWorkflow } from './TransferenciaWorkflow';

export const config = {
	key: 'epp',
	shortLabel: 'EPP',
	label: 'EPP - Elementos de Protección Personal',
	description: 'Guías para retiro o transferencia de EPP',

	// Tipos de operación disponibles
	operationTypes: [
		{ value: 'retiro', label: 'Retiro de Bodega EPP' },
		{ value: 'transferencia', label: 'Transferencia a Bodega EPP' },
	],

	// Campos adicionales que este workflow necesita
	additionalFields: {
		getInitialValues: () => ({
			epp_operation_type: '',
			warehouse_origin_id: '',
			pick_person_id: '',
		}),
		getValidationSchema: Yup => ({
			epp_operation_type: Yup.string().required(
				'Seleccione tipo de operación'
			),
			warehouse_origin_id: Yup.string().when('epp_operation_type', {
				is: 'transferencia',
				then: schema => schema.required('Seleccione bodega origen'),
				otherwise: schema => schema,
			}),
			pick_person_id: Yup.string().when('epp_operation_type', {
				is: 'retiro',
				then: schema => schema.required('Seleccione persona que retira'),
				otherwise: schema => schema,
			}),
		}),
	},
};

/**
 * Ejecutor del workflow
 * @param {Function} onProgress - Callback (stepId, status, message?) para reportar progreso
 */
export const execute = async (dispatch, dteResult, formValues, onProgress) => {
	const { epp_operation_type } = formValues;

	if (epp_operation_type === 'retiro') {
		return await RetiroWorkflow.execute(dispatch, dteResult, formValues, onProgress);
	} else if (epp_operation_type === 'transferencia') {
		return await TransferenciaWorkflow.execute(
			dispatch,
			dteResult,
			formValues,
			onProgress
		);
	}

	throw new Error(`Operación EPP no válida: ${epp_operation_type}`);
};

/**
 * Obtiene los pasos de progreso que ejecutará el workflow
 */
export const getProgressSteps = (formValues) => {
	const { epp_operation_type } = formValues;

	if (epp_operation_type === 'retiro') {
		return [
			{ id: 'wf_pdf', label: 'Generando PDF de la guía' },
			{ id: 'wf_upload', label: 'Subiendo documento' },
			{ id: 'wf_guide', label: 'Registrando guía' },
			{ id: 'wf_deliver', label: 'Ejecutando retiro de EPPs' },
		];
	} else if (epp_operation_type === 'transferencia') {
		return [
			{ id: 'wf_pdf', label: 'Generando PDF de la guía' },
			{ id: 'wf_upload', label: 'Subiendo documento' },
			{ id: 'wf_guide', label: 'Registrando guía' },
			{ id: 'wf_transfer', label: 'Ejecutando transferencia' },
		];
	}

	return [];
};

/**
 * Obtiene los pasos de rollback para este workflow
 */
export const getRollbackSteps = () => {
	return [
		{ id: 'rb_annul_guide', label: 'Anulando guía interna' }
	];
};

/**
 * Validación pre-submit específica del workflow
 */
export const preValidate = async formValues => {
	const errors = [];
	const { epp_operation_type } = formValues;

	// Verificar que hay items
	if (!formValues.items || formValues.items.length === 0) {
		errors.push('Debe agregar al menos un ítem');
	}

	// Verificar metadata de items según tipo de operación
	if (epp_operation_type === 'retiro') {
		// Para retiro: verificar que los items tengan IDs de EPP
		const itemsWithoutPPE = formValues.items?.filter(item => {
			const moduleVars = item._module_vars || {};
			return (
				!moduleVars.personal_protective_equipment_ids?.length &&
				!moduleVars.personal_protective_equipment_id
			);
		});
		if (itemsWithoutPPE?.length > 0) {
			errors.push(
				'Todos los ítems deben provenir de EPPs seleccionados'
			);
		}
	} else if (epp_operation_type === 'transferencia') {
		// Para transferencia: verificar warehouse_item_id
		const itemsWithoutMetadata = formValues.items?.filter(
			item => !item._module_vars?.warehouse_item_id
		);
		if (itemsWithoutMetadata?.length > 0) {
			errors.push(
				'Todos los ítems deben ser seleccionados desde el catálogo de bodega'
			);
		}
	}

	return {
		valid: errors.length === 0,
		errors,
	};
};

/**
 * Transformación de items para el payload DTE
 */
export const transformItemsForDTE = (items, formValues) => {
	// Mapeo específico de EPP si es necesario
	return items;
};

